import { DateOnly } from '@if/core/date'
import { formatCurrency } from '@if/core/utils'
import { renderSelectMenuItems, useIfUser } from '@if/mui'
import { EOperationStatus, EPaymentMethod } from '@src/types/constants'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { JSX, useMemo } from 'react'

const MAX_OPTIONS = 20

/** How many payments after the move an agent may raise to cover the gap. */
export const MAX_PAYMENTS_TO_RAISE = 6

export const useValidPaymentOperationsForCalendarShift = (simulation: CalendarSimulation): JSX.Element[] => {
  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments
  return useMemo(() => {
    const minimumDate = simulation.getNextValidActionDate(EPaymentMethod.PreAuthorizedDebit, canBypassAcpLock)
    const shiftable = simulation.customerPaymentOperations
      .filter(
        ({ operation }) =>
          operation.operationStatusId === EOperationStatus.Planned && operation.effectiveDate >= minimumDate,
      )
      .slice(0, MAX_OPTIONS)

    return renderSelectMenuItems(shiftable, ({ operation }) => ({
      value: operation.effectiveDate.toString(),
      label: `${operation.effectiveDate.toString()} - ${formatCurrency(operation.amount)}`,
    }))
  }, [simulation, canBypassAcpLock])
}

/**
 * The payments the move action raises: the first `count` customer payments falling on or
 * after the date being moved. Derived rather than stored, so the agent's field is the count
 * they typed and the action carries what that count means.
 */
export const getPaymentToRaiseIds = (simulation: CalendarSimulation, fromDate: DateOnly, count: number): number[] =>
  simulation.customerPaymentOperations
    .filter(({ operation }) => operation.effectiveDate.getTime() >= fromDate.getTime())
    .slice(0, count)
    .map(({ operation }) => operation.id)
